// src/components/SurveysTab.jsx - COMPLETE VERSION
import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Modal,
  Pagination,
  Dropdown,
  Tabs,
  Tab,
  Accordion
} from 'react-bootstrap';
import { surveysAPI, organizationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useRef } from 'react';
import TemplateDuplicatorModal from './TemplateDuplicatorModal';

const SurveysTab = () => {
  const { user } = useAuth();
  
  // Состояния
  const [surveys, setSurveys] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showDuplicatorModal, setShowDuplicatorModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // Состояния для удаления вопроса
  const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(false);

  // Фильтры и пагинация
  const [pagination, setPagination] = useState({
    page: 2,
    limit: 100,
    total: 0,
    pages: 1
  });
  
  const [filters, setFilters] = useState({
    search: '',
    organization_id: null,
    status: ''
  });
  
  // Данные форм
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    instructions: '',
    organization_id: null,
    is_anonymous: true,
    require_email: false,
    allow_multiple_submissions: false,
    max_submissions_per_user: 1,
    starts_at: '',
    ends_at: '',
    status: 'draft'
  });
  
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'single_choice',
    description: '',
    is_required: true,
    order_index: 0,
    options: ['', ''],
    min_length: null,
    max_length: null,
    min_value: 1,
    max_value: 5
  });
  
  // Выбранные данные
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSurveyQuestions, setSelectedSurveyQuestions] = useState([]);
  const [selectedSurveyAnalytics, setSelectedSurveyAnalytics] = useState(null);
  const [selectedSurveySubmissions, setSelectedSurveySubmissions] = useState([]);
  
  // Списки для выбора
  const [organizations, setOrganizations] = useState([]);
  const [questionTypes] = useState([
    { value: 'single_choice', label: 'Одиночный выбор' },
    { value: 'multiple_choice', label: 'Множественный выбор' },
    { value: 'text', label: 'Текстовый ответ' },
    { value: 'rating', label: 'Оценка (рейтинг)' },
    { value: 'yes_no', label: 'Да/Нет' }
  ]);
  
  // Загрузка анкет
  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        skip: (pagination.page - 1) * pagination.limit,
        limit: pagination.limit,
        organization_id: filters.organization_id || undefined,
        status: filters.status || undefined
      };
      
      const response = await surveysAPI.getSurveys(params);
      
      if (response) {
        setSurveys(response);
        setPagination(prev => ({
          ...prev,
          total: response.length,
          pages: Math.ceil(response.length / pagination.limit)
        }));
      } else {
        setSurveys([]);
      }
    } catch (err) {
      console.error('Error loading surveys:', err);
      setError('Ошибка при загрузке анкет');
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Загрузка шаблонов
  const loadTemplates = async () => {
    try {
      const response = await surveysAPI.getTemplates();
      setTemplates(response || []);
    } catch (err) {
      console.error('Error loading templates:', err);
      setTemplates([]);
    }
  };
  
  // Загрузка организаций для выбора
  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.getOrganizations({
        skip: 0,
        limit: 100,
        only_active: true
      });
      
      if (response && response.items) {
        setOrganizations(response.items);
      }
    } catch (err) {
      console.error('Error loading organizations:', err);
    }
  };
  
  // Загрузка при монтировании
  useEffect(() => {
    if (activeTab === 'templates') {
      loadTemplates();
    } else {
      loadSurveys();
      loadOrganizations();
    }
  }, [pagination.page, activeTab, filters.organization_id, filters.status]);
  
  // Обработчики изменений формы анкеты
  const handleSurveyFormChange = (field, value) => {
    setSurveyForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Обработчики изменений формы вопроса
  const handleQuestionFormChange = (field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Добавление опции к вопросу
  const addQuestionOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };
  
  // Изменение опции вопроса
  const updateQuestionOption = (index, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm(prev => ({
      ...prev,
      options: newOptions
    }));
  };
  
  // Удаление опции вопроса
  const removeQuestionOption = (index) => {
    const newOptions = questionForm.options.filter((_, i) => i !== index);
    setQuestionForm(prev => ({
      ...prev,
      options: newOptions
    }));
  };
  
  // Создание анкеты
  const handleCreateSurvey = async () => {
    try {
      setError('');
      
      // Валидация
      if (!surveyForm.title || !surveyForm.organization_id) {
        setError('Название и организация обязательны для заполнения');
        return;
      }
      
      // Подготовка данных
      const payload = {
        title: surveyForm.title.trim(),
        description: surveyForm.description?.trim() || null,
        instructions: surveyForm.instructions?.trim() || null,
        organization_id: surveyForm.organization_id,
        is_anonymous: surveyForm.is_anonymous,
        require_email: surveyForm.require_email,
        allow_multiple_submissions: surveyForm.allow_multiple_submissions,
        max_submissions_per_user: surveyForm.max_submissions_per_user,
        starts_at: surveyForm.starts_at || null,
        ends_at: surveyForm.ends_at || null
      };
      
      console.log('📤 Creating survey:', payload);
      
      const response = await surveysAPI.createSurvey(payload);
      
      if (response) {
        // Сброс формы
        setSurveyForm({
          title: '',
          description: '',
          instructions: '',
          organization_id: null,
          is_anonymous: true,
          require_email: false,
          allow_multiple_submissions: false,
          max_submissions_per_user: 1,
          starts_at: '',
          ends_at: '',
          status: 'draft'
        });
        setShowCreateModal(false);
        
        // Обновление списка
        await loadSurveys();
        
        alert('Анкета успешно создана!');
      }
    } catch (err) {
      console.error('❌ Error creating survey:', err);
      setError(err.response?.data?.detail || 'Ошибка при создании анкеты');
    }
  };
  
  // Создание шаблона
const handleCreateTemplate = async () => {
  try {
    setError('');
    
    if (!surveyForm.title) {
      setError('Название шаблона обязательно');
      return;
    }
    
    // ✅ Убираем organization_id полностью для шаблона
    const payload = {
      title: surveyForm.title.trim(),
      description: surveyForm.description?.trim() || null,
      instructions: surveyForm.instructions?.trim() || null,
      is_anonymous: surveyForm.is_anonymous,
      require_email: surveyForm.require_email,
      allow_multiple_submissions: surveyForm.allow_multiple_submissions,
      max_submissions_per_user: surveyForm.max_submissions_per_user,
      starts_at: surveyForm.starts_at || null,
      ends_at: surveyForm.ends_at || null
    };
    
    console.log('📤 Creating template with payload:', payload);
    
    const response = await surveysAPI.createTemplate(payload);
    
    if (response) {
      // Сброс формы
      setSurveyForm({
        title: '',
        description: '',
        instructions: '',
        organization_id: null,
        is_anonymous: true,
        require_email: false,
        allow_multiple_submissions: false,
        max_submissions_per_user: 1,
        starts_at: '',
        ends_at: '',
        status: 'draft'
      });
      setShowTemplateModal(false);
      await loadTemplates();
      alert('Шаблон успешно создан!');
    }
  } catch (err) {
    console.error('❌ Error creating template:', err);
    let errorMessage = 'Ошибка при создании шаблона';
    
    if (err.response?.status === 422) {
      const errors = err.response?.data?.detail;
      if (Array.isArray(errors)) {
        errorMessage = errors.map(e => e.msg).join('; ');
      } else {
        errorMessage = errors?.message || 'Ошибка валидации данных';
      }
    } else if (err.response?.data?.detail) {
      errorMessage = err.response.data.detail;
    }
    
    setError(errorMessage);
  }
};

  
  // Обновление анкеты
  const handleUpdateSurvey = async () => {
    try {
      setError('');
      
      if (!selectedSurvey) return;
      
      const updateData = {};
      
      // Добавляем только измененные поля
      Object.keys(surveyForm).forEach(key => {
        if (surveyForm[key] !== '' && surveyForm[key] !== null) {
          updateData[key] = surveyForm[key];
        }
      });
      
      // Конвертируем пустые строки в null
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          updateData[key] = null;
        }
      });
      
      console.log('📤 Updating survey:', updateData);
      
      const response = await surveysAPI.updateSurvey(selectedSurvey.id, updateData);
      
      if (response) {
        setShowEditModal(false);
        if (selectedSurvey.is_template) {
          await loadTemplates();
        } else {
          await loadSurveys();
        }
        alert('Анкета успешно обновлена!');
      }
    } catch (err) {
      console.error('❌ Error updating survey:', err);
      setError(err.response?.data?.detail || 'Ошибка при обновлении анкеты');
    }
  };
  
  // Добавление вопроса
  const handleAddQuestion = async () => {
    try {
      setError('');
      
      if (!selectedSurvey) return;
      
      if (!questionForm.question_text) {
        setError('Текст вопроса обязателен');
        return;
      }
      
      // Подготовка данных вопроса
      const questionData = {
        question_text: questionForm.question_text.trim(),
        question_type: questionForm.question_type,
        description: questionForm.description?.trim() || null,
        is_required: questionForm.is_required,
        order_index: questionForm.order_index,
        options: questionForm.question_type.includes('choice') ? 
                questionForm.options.filter(opt => opt.trim() !== '') : null,
        min_length: questionForm.min_length || null,
        max_length: questionForm.max_length || null,
        min_value: questionForm.min_value || null,
        max_value: questionForm.max_value || null
      };
      
      console.log('📤 Adding question:', questionData);
      
      const response = await surveysAPI.addQuestion(selectedSurvey.id, questionData);
      
      if (response) {
        // Сброс формы вопроса
        setQuestionForm({
          question_text: '',
          question_type: 'single_choice',
          description: '',
          is_required: true,
          order_index: 0,
          options: ['', ''],
          min_length: null,
          max_length: null,
          min_value: 1,
          max_value: 5
        });
        
        // Обновление списка вопросов
        await loadSurveyQuestions(selectedSurvey.id);
        alert('Вопрос успешно добавлен!');
      }
    } catch (err) {
      console.error('❌ Error adding question:', err);
      setError(err.response?.data?.detail || 'Ошибка при добавлении вопроса');
    }
  };
  
  // Удаление вопроса
  const handleDeleteQuestion = async () => {
    if (!questionToDelete || !selectedSurvey) return;
    
    try {
      setDeletingQuestion(true);
      setError('');
      
      console.log('🗑️ Deleting question:', questionToDelete.id);
      
      await surveysAPI.deleteQuestion(selectedSurvey.id, questionToDelete.id);
      
      // Обновляем список вопросов
      await loadSurveyQuestions(selectedSurvey.id);
      
      // Показываем успешное уведомление
      alert('Вопрос успешно удален!');
      
      // Закрываем модальное окно
      setShowDeleteQuestionModal(false);
      setQuestionToDelete(null);
      
    } catch (err) {
      console.error('❌ Error deleting question:', err);
      
      let errorMessage = 'Не удалось удалить вопрос';
      
      if (err.response?.status === 403) {
        errorMessage = 'У вас нет прав на удаление этого вопроса';
      } else if (err.response?.status === 404) {
        errorMessage = 'Вопрос не найден';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
      alert(errorMessage);
      
    } finally {
      setDeletingQuestion(false);
    }
  };
  
  // Открытие модального окна удаления вопроса
  const openDeleteQuestionModal = (question, e) => {
    e?.stopPropagation();
    setQuestionToDelete(question);
    setShowDeleteQuestionModal(true);
  };
  
  // Архивация анкеты
  const handleArchiveSurvey = async () => {
    try {
      if (!selectedSurvey) return;
      
      await surveysAPI.archiveSurvey(selectedSurvey.id);
      
      setShowDeleteModal(false);
      await loadSurveys();
      alert('Анкета перемещена в архив');
    } catch (err) {
      console.error('Error archiving survey:', err);
      setError('Ошибка при архивации анкеты');
    }
  };
  
  // Загрузка вопросов анкеты
  const loadSurveyQuestions = async (surveyId) => {
    try {
      const survey = await surveysAPI.getSurvey(surveyId);
      if (survey && survey.questions) {
        // Сортируем вопросы по order_index
        const sortedQuestions = [...survey.questions].sort((a, b) => a.order_index - b.order_index);
        setSelectedSurveyQuestions(sortedQuestions);
      } else {
        setSelectedSurveyQuestions([]);
      }
    } catch (err) {
      console.error('Error loading questions:', err);
      setSelectedSurveyQuestions([]);
    }
  };
  
  // Загрузка аналитики анкеты
  const loadSurveyAnalytics = async (surveyId) => {
    try {
      const analytics = await surveysAPI.getSurveyAnalytics(surveyId);
      setSelectedSurveyAnalytics(analytics);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setSelectedSurveyAnalytics(null);
    }
  };
  
  // Загрузка отправок анкеты
  const loadSurveySubmissions = async (surveyId) => {
    try {
      const submissions = await surveysAPI.getSurveySubmissions(surveyId);
      setSelectedSurveySubmissions(submissions);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setSelectedSurveySubmissions([]);
    }
  };
  
  // Открытие модальных окон
  const openEditModal = (survey) => {
    setSelectedSurvey(survey);
    setSurveyForm({
      title: survey.title || '',
      description: survey.description || '',
      instructions: survey.instructions || '',
      organization_id: survey.organization_id || null,
      is_anonymous: survey.is_anonymous || true,
      require_email: survey.require_email || false,
      allow_multiple_submissions: survey.allow_multiple_submissions || false,
      max_submissions_per_user: survey.max_submissions_per_user || 1,
      starts_at: survey.starts_at || '',
      ends_at: survey.ends_at || '',
      status: survey.status || 'draft'
    });
    setShowEditModal(true);
  };
  
  const openQuestionsModal = async (survey) => {
    setSelectedSurvey(survey);
    await loadSurveyQuestions(survey.id);
    setShowQuestionsModal(true);
  };
  
  const openAnalyticsModal = async (survey) => {
    setSelectedSurvey(survey);
    await loadSurveyAnalytics(survey.id);
    setShowAnalyticsModal(true);
  };
  
  const openSubmissionsModal = async (survey) => {
    setSelectedSurvey(survey);
    await loadSurveySubmissions(survey.id);
    setShowSubmissionsModal(true);
  };
  
  const openDeleteModal = (survey) => {
    setSelectedSurvey(survey);
    setShowDeleteModal(true);
  };
  
  const openDetailsModal = (survey) => {
    setSelectedSurvey(survey);
    setShowDetailsModal(true);
  };
  
  const openDuplicatorModal = (template) => {
    setSelectedTemplate(template);
    setShowDuplicatorModal(true);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Получение статуса анкеты
  const getSurveyStatus = (survey) => {
    switch (survey.status) {
      case 'draft': return { variant: 'secondary', text: 'Черновик' };
      case 'active': return { variant: 'success', text: 'Активна' };
      case 'archived': return { variant: 'danger', text: 'В архиве' };
      default: return { variant: 'secondary', text: 'Неизвестно' };
    }
  };
  
  // Проверка активности анкеты
  const getSurveyActivity = (survey) => {
    const now = new Date();
    const startsAt = survey.starts_at ? new Date(survey.starts_at) : null;
    const endsAt = survey.ends_at ? new Date(survey.ends_at) : null;
    
    if (survey.status !== 'active') return false;
    if (startsAt && now < startsAt) return false;
    if (endsAt && now > endsAt) return false;
    
    return true;
  };
  
  // Обработка поиска
  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab === 'templates') {
      loadTemplates();
    } else {
      loadSurveys();
    }
  };
  
  // Обработка изменения страницы
  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, page: pageNumber }));
  };
  
  // Обработка изменения фильтров
  const handleFilterChange = (filter, value) => {
    setFilters(prev => ({ ...prev, [filter]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Обработка изменения вкладки
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    if (tab === 'templates') {
      loadTemplates();
    } else {
      // Устанавливаем фильтр по статусу в зависимости от вкладки
      let statusFilter = '';
      if (tab === 'active') statusFilter = 'active';
      if (tab === 'draft') statusFilter = 'draft';
      if (tab === 'archived') statusFilter = 'archived';
      
      setFilters(prev => ({ ...prev, status: statusFilter }));
      setPagination(prev => ({ ...prev, page: 1 }));
      loadSurveys();
    }
  };
  
  // Копирование публичной ссылки
  const copyPublicLink = (survey) => {
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/survey/${survey.public_id}`;
    
    navigator.clipboard.writeText(publicUrl)
      .then(() => alert('Ссылка скопирована в буфер обмена!'))
      .catch(err => console.error('Ошибка копирования:', err));
  };
  
  const generateQRCode = (survey) => {
    const baseUrl = window.location.origin;
    const publicUrl = `${baseUrl}/survey/${survey.public_id}`;
    setQrCodeUrl(publicUrl);
    setShowQRModal(true);
  };

  // Рендер пагинации
  const renderPagination = () => {
    const items = [];
    
    for (let number = 1; number <= pagination.pages; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === pagination.page}
          onClick={() => handlePageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    
    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev 
          disabled={pagination.page === 1}
          onClick={() => handlePageChange(pagination.page - 1)}
        />
        {items}
        <Pagination.Next 
          disabled={pagination.page === pagination.pages}
          onClick={() => handlePageChange(pagination.page + 1)}
        />
      </Pagination>
    );
  };
  
  const exportSurveyAnalytics = async (survey) => {
    try {
        const response = await fetch(`/api/v1/surveys/${survey.id}/export`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'X-API-Key': window.env.VITE_API_KEY
            }
        });
        
        if (!response.ok) {
            throw new Error('Export failed');
        }
        
        // Скачиваем файл
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey_${survey.id}_${survey.title.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
    } catch (err) {
        console.error('Error exporting survey:', err);
        alert('Ошибка при экспорте данных');
    }
};


  // Рендер таблицы анкет
  const renderSurveysTable = () => {
    if (loading) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Загрузка анкет...</p>
          </Card.Body>
        </Card>
      );
    }
    
    if (error) {
      return (
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <Button 
            variant="outline-danger" 
            size="sm" 
            className="ms-3"
            onClick={loadSurveys}
          >
            Повторить
          </Button>
        </Alert>
      );
    }
    
    if (surveys.length === 0) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="bi bi-clipboard-data display-1 text-muted opacity-50 mb-3"></i>
            <h5>Анкеты не найдены</h5>
            <p className="text-muted mb-0">
              {filters.search 
                ? 'Попробуйте изменить условия поиска' 
                : 'Создайте первую анкету'}
            </p>
            {!filters.search && (
              <Button 
                variant="primary" 
                className="mt-3"
                onClick={() => setShowCreateModal(true)}
              >
                <i className="bi bi-plus-lg me-2"></i>
                Создать анкету
              </Button>
            )}
          </Card.Body>
        </Card>
      );
    }
    
    return (
      <Card>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th width="50">ID</th>
                  <th>Название</th>
                  <th>Организация</th>
                  <th>Статус</th>
                  <th>Период проведения</th>
                  <th>Отправки</th>
                  <th width="180">Действия</th>
                </tr>
              </thead>
              <tbody>
                {surveys.map(survey => {
                  const status = getSurveyStatus(survey);
                  const isActive = getSurveyActivity(survey);
                  
                  return (
                    <tr key={survey.id}>
                      <td>
                        <code className="text-muted">#{survey.id}</code>
                      </td>
                      <td>
                        <div className="fw-semibold hover-primary cursor-pointer"
                             onClick={() => openDetailsModal(survey)}>
                          {survey.title}
                        </div>
                        {survey.description && (
                          <small className="text-muted d-block mt-1">
                            {survey.description.length > 50
                              ? `${survey.description.substring(0, 50)}...`
                              : survey.description}
                          </small>
                        )}
                      </td>
                      <td>
                        {survey.organization_name || '—'}
                      </td>
                      <td>
                        <Badge bg={status.variant}>
                          {status.text}
                        </Badge>
                        {isActive && (
                          <Badge bg="success" className="ms-1">
                            <i className="bi bi-play-fill me-1"></i>
                            Активна
                          </Badge>
                        )}
                        <div className="text-muted small mt-1">
                          Вопросов: {survey.questions_count || 0}
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          {survey.starts_at ? (
                            <div>С: {formatDate(survey.starts_at)}</div>
                          ) : 'Без ограничений'}
                          {survey.ends_at && (
                            <div>По: {formatDate(survey.ends_at)}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold">
                          {survey.submission_count || 0}
                        </div>
                        <div className="text-muted small">
                          {survey.is_anonymous ? 'Анонимная' : 'Именная'}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Просмотреть"
                            onClick={() => openDetailsModal(survey)}
                          >
                            <i className="bi bi-eye"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Сгенерировать QR-код"
                            onClick={() => generateQRCode(survey)}
                          >
                            <i className="bi bi-qr-code"></i>
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            title="Редактировать"
                            onClick={() => openEditModal(survey)}
                            disabled={survey.status === 'archived'}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Dropdown>
                            <Dropdown.Toggle 
                              variant="outline-secondary" 
                              size="sm" 
                              id={`dropdown-${survey.id}`}
                              disabled={survey.status === 'archived'}
                            />
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openQuestionsModal(survey)}>
                                <i className="bi bi-question-circle me-2"></i>
                                Вопросы
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => copyPublicLink(survey)}>
                                <i className="bi bi-link me-2"></i>
                                Скопировать ссылку
                              </Dropdown.Item>
                              {/* НОВЫЙ ПУНКТ - СКАЧАТЬ QR-КОД */}
                              <Dropdown.Item onClick={() => {
                                const baseUrl = window.location.origin;
                                const publicUrl = `${baseUrl}/survey/${survey.public_id}`;
                                setQrCodeUrl(publicUrl);
                                setSelectedSurvey(survey);
                                setShowQRModal(true);
                              }}>
                                <i className="bi bi-qr-code me-2"></i>
                                QR-код
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => openAnalyticsModal(survey)}>
                                <i className="bi bi-graph-up me-2"></i>
                                Аналитика
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => exportSurveyAnalytics(survey)}>
                                <i className="bi bi-file-earmark-excel me-2 text-success"></i>
                                Экспорт в Excel
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => openSubmissionsModal(survey)}>
                                <i className="bi bi-list-check me-2"></i>
                                Отправки ({survey.submission_count || 0})
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                className="text-danger"
                                onClick={() => openDeleteModal(survey)}
                              >
                                <i className="bi bi-archive me-2"></i>
                                В архив
                              </Dropdown.Item>

                            </Dropdown.Menu>
                          </Dropdown>

                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };
  
  // Рендер таблицы шаблонов
  const renderTemplatesTable = () => {
    if (loading) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Загрузка шаблонов...</p>
          </Card.Body>
        </Card>
      );
    }
    
    if (error) {
      return (
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <Button 
            variant="outline-danger" 
            size="sm" 
            className="ms-3"
            onClick={loadTemplates}
          >
            Повторить
          </Button>
        </Alert>
      );
    }
    
    if (templates.length === 0) {
      return (
        <Card>
          <Card.Body className="text-center py-5">
            <i className="bi bi-file-text display-1 text-muted opacity-50 mb-3"></i>
            <h5>Шаблоны не найдены</h5>
            <p className="text-muted mb-0">
              Создайте первый шаблон анкеты
            </p>
            <Button 
              variant="primary" 
              className="mt-3"
              onClick={() => setShowTemplateModal(true)}
            >
              <i className="bi bi-plus-lg me-2"></i>
              Создать шаблон
            </Button>
          </Card.Body>
        </Card>
      );
    }
    
    return (
      <>
        <Card>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th width="50">ID</th>
                    <th>Название шаблона</th>
                    <th>Описание</th>
                    <th>Вопросов</th>
                    <th>Создан</th>
                    <th width="200">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(template => (
                    <tr key={template.id}>
                      <td>
                        <code className="text-muted">#{template.id}</code>
                      </td>
                      <td>
                        <div className="fw-semibold">{template.title}</div>
                        {template.instructions && (
                          <small className="text-muted d-block mt-1">
                            {template.instructions.substring(0, 50)}...
                          </small>
                        )}
                      </td>
                      <td>
                        {template.description ? (
                          <small className="text-muted">
                            {template.description.substring(0, 100)}...
                          </small>
                        ) : '—'}
                      </td>
                      <td>
                        <Badge bg="info">
                          {template.questions_count || 0} вопросов
                        </Badge>
                      </td>
                      <td>
                        <small>{formatDate(template.created_at)}</small>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Редактировать"
                            onClick={() => openEditModal(template)}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            title="Добавить вопросы"
                            onClick={async () => {
                              await loadSurveyQuestions(template.id);
                              setSelectedSurvey(template);
                              setShowQuestionsModal(true);
                            }}
                          >
                            <i className="bi bi-question-circle"></i>
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            title="Размножить для организаций"
                            onClick={() => openDuplicatorModal(template)}
                          >
                            <i className="bi bi-files"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
        
        <div className="mt-3">
          <Button 
            variant="primary" 
            onClick={() => setShowTemplateModal(true)}
            className="d-flex align-items-center gap-2"
          >
            <i className="bi bi-plus-lg"></i>
            Создать новый шаблон
          </Button>
        </div>
      </>
    );
  };
  
  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0">
            <i className="bi bi-clipboard-data me-2"></i>
            Управление анкетами
          </h1>
          <p className="text-muted mb-0">
            Создание, редактирование и управление опросами для оценки качества
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-center">
          {(user?.role === 'admin' || user?.role === 'moderator') && activeTab !== 'templates' && (
            <Button 
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              className="d-flex align-items-center gap-2"
            >
              <i className="bi bi-plus-lg"></i>
              Создать анкету
            </Button>
          )}
        </Col>
      </Row>
      
      {/* Фильтры и поиск (только для вкладок с анкетами) */}
      {activeTab !== 'templates' && (
        <Card className="mb-4">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col md={4}>
                <Form onSubmit={handleSearch}>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Поиск по названию или описанию..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                    <Button variant="primary" type="submit">
                      <i className="bi bi-search"></i>
                    </Button>
                  </InputGroup>
                </Form>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={filters.organization_id || ''}
                  onChange={(e) => handleFilterChange('organization_id', e.target.value || null)}
                >
                  <option value="">Все организации</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || '')}
                >
                  <option value="">Все статусы</option>
                  <option value="draft">Черновики</option>
                  <option value="active">Активные</option>
                  <option value="archived">Архивные</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
      
      {/* Вкладки */}
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabChange}
        className="mb-4"
      >
        <Tab eventKey="all" title="Все анкеты">
          <div className="mt-3">
            {renderSurveysTable()}
          </div>
        </Tab>
        <Tab eventKey="active" title="Активные">
          <div className="mt-3">
            {renderSurveysTable()}
          </div>
        </Tab>
        <Tab eventKey="draft" title="Черновики">
          <div className="mt-3">
            {renderSurveysTable()}
          </div>
        </Tab>
        <Tab eventKey="archived" title="Архив">
          <div className="mt-3">
            {renderSurveysTable()}
          </div>
        </Tab>
        <Tab eventKey="templates" title="📋 Шаблоны">
          <div className="mt-3">
            {renderTemplatesTable()}
          </div>
        </Tab>
      </Tabs>
      
      {/* Пагинация (только для анкет) */}
      {activeTab !== 'templates' && pagination.pages > 1 && renderPagination()}
      
      {/* Модальные окна */}
      <CreateSurveyModal 
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        surveyForm={surveyForm}
        handleSurveyFormChange={handleSurveyFormChange}
        handleCreateSurvey={handleCreateSurvey}
        organizations={organizations}
        error={error}
      />
      
      <EditSurveyModal 
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        surveyForm={surveyForm}
        handleSurveyFormChange={handleSurveyFormChange}
        handleUpdateSurvey={handleUpdateSurvey}
        organizations={organizations}
        error={error}
        selectedSurvey={selectedSurvey}
      />
      
      <DeleteSurveyModal 
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        handleArchiveSurvey={handleArchiveSurvey}
        selectedSurvey={selectedSurvey}
      />
      
      <SurveyDetailsModal 
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        selectedSurvey={selectedSurvey}
        formatDate={formatDate}
        getSurveyStatus={getSurveyStatus}
        getSurveyActivity={getSurveyActivity}
      />
      
      <SurveyQuestionsModal 
        show={showQuestionsModal}
        onHide={() => setShowQuestionsModal(false)}
        selectedSurvey={selectedSurvey}
        selectedSurveyQuestions={selectedSurveyQuestions}
        questionForm={questionForm}
        handleQuestionFormChange={handleQuestionFormChange}
        addQuestionOption={addQuestionOption}
        updateQuestionOption={updateQuestionOption}
        removeQuestionOption={removeQuestionOption}
        handleAddQuestion={handleAddQuestion}
        questionTypes={questionTypes}
        error={error}
        onDeleteQuestion={openDeleteQuestionModal}
      />
      
      <SurveyAnalyticsModal 
        show={showAnalyticsModal}
        onHide={() => setShowAnalyticsModal(false)}
        selectedSurvey={selectedSurvey}
        selectedSurveyAnalytics={selectedSurveyAnalytics}
      />
      
      <SurveySubmissionsModal 
        show={showSubmissionsModal}
        onHide={() => setShowSubmissionsModal(false)}
        selectedSurvey={selectedSurvey}
        selectedSurveySubmissions={selectedSurveySubmissions}
        formatDate={formatDate}
      />
      
      <QRCodeModal 
        show={showQRModal}
        onHide={() => setShowQRModal(false)}
        qrCodeUrl={qrCodeUrl}
        selectedSurvey={selectedSurvey}
      />
      
      {/* Модальное окно создания шаблона */}
      <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-file-text me-2"></i>
            Создание шаблона анкеты
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            Шаблон не привязан к организации. После создания вы сможете размножить его для любых организаций.
          </Alert>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Название шаблона <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название шаблона"
                value={surveyForm.title}
                onChange={(e) => handleSurveyFormChange('title', e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Описание шаблона</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Опишите цель и содержание анкеты"
                value={surveyForm.description}
                onChange={(e) => handleSurveyFormChange('description', e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Инструкции для респондентов</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Укажите инструкции по заполнению анкеты"
                value={surveyForm.instructions}
                onChange={(e) => handleSurveyFormChange('instructions', e.target.value)}
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is-anonymous"
                    label="Анонимная анкета"
                    checked={surveyForm.is_anonymous}
                    onChange={(e) => handleSurveyFormChange('is_anonymous', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="require-email"
                    label="Требовать email"
                    checked={surveyForm.require_email}
                    onChange={(e) => handleSurveyFormChange('require_email', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="allow-multiple"
                    label="Разрешить несколько отправок"
                    checked={surveyForm.allow_multiple_submissions}
                    onChange={(e) => handleSurveyFormChange('allow_multiple_submissions', e.target.checked)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Дата начала</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={surveyForm.starts_at}
                    onChange={(e) => handleSurveyFormChange('starts_at', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Дата окончания</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={surveyForm.ends_at}
                    onChange={(e) => handleSurveyFormChange('ends_at', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>
            Отмена
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateTemplate}
            disabled={!surveyForm.title}
          >
            <i className="bi bi-check-lg me-2"></i>
            Создать шаблон
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Модальное окно удаления вопроса */}
      <Modal show={showDeleteQuestionModal} onHide={() => setShowDeleteQuestionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Удаление вопроса
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {questionToDelete && (
            <>
              <p>
                Вы уверены, что хотите удалить вопрос?
              </p>
              <Alert variant="warning" className="mb-3">
                <div className="fw-semibold mb-2">"{questionToDelete.question_text}"</div>
                <div className="small text-muted">
                  Тип вопроса: {
                    questionToDelete.question_type === 'single_choice' ? 'Одиночный выбор' :
                    questionToDelete.question_type === 'multiple_choice' ? 'Множественный выбор' :
                    questionToDelete.question_type === 'text' ? 'Текстовый ответ' :
                    questionToDelete.question_type === 'rating' ? 'Оценка' :
                    questionToDelete.question_type === 'yes_no' ? 'Да/Нет' : questionToDelete.question_type
                  }
                </div>
              </Alert>
              <Alert variant="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Внимание!</strong> Все ответы на этот вопрос будут безвозвратно удалены. 
                Это действие нельзя отменить.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteQuestionModal(false)} disabled={deletingQuestion}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteQuestion} disabled={deletingQuestion}>
            {deletingQuestion ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Удаление...
              </>
            ) : (
              <>
                <i className="bi bi-trash me-2"></i>
                Удалить вопрос
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Модальное окно размножения шаблона */}
      <TemplateDuplicatorModal
        show={showDuplicatorModal}
        onHide={() => setShowDuplicatorModal(false)}
        template={selectedTemplate}
        onSuccess={(result) => {
          console.log('Duplication result:', result);
          alert(`Создано ${result.created_surveys.length} анкет!`);
          setActiveTab('all');
          loadSurveys();
        }}
      />
    </Container>
  );
};

// Модальное окно создания анкеты
const CreateSurveyModal = ({ 
  show, 
  onHide, 
  surveyForm, 
  handleSurveyFormChange, 
  handleCreateSurvey,
  organizations,
  error 
}) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>
        <i className="bi bi-plus-circle me-2"></i>
        Создание анкеты
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>
                Название анкеты <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название анкеты"
                value={surveyForm.title}
                onChange={(e) => handleSurveyFormChange('title', e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Описание анкеты</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Опишите цель и содержание анкеты"
                value={surveyForm.description}
                onChange={(e) => handleSurveyFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Инструкции для респондентов</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Укажите инструкции по заполнению анкеты"
                value={surveyForm.instructions}
                onChange={(e) => handleSurveyFormChange('instructions', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Организация <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={surveyForm.organization_id || ''}
                onChange={(e) => handleSurveyFormChange('organization_id', parseInt(e.target.value) || null)}
                required
              >
                <option value="">Выберите организацию</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={surveyForm.status}
                onChange={(e) => handleSurveyFormChange('status', e.target.value)}
              >
                <option value="draft">Черновик</option>
                <option value="active">Активна</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is-anonymous"
                label="Анонимная анкета"
                checked={surveyForm.is_anonymous}
                onChange={(e) => handleSurveyFormChange('is_anonymous', e.target.checked)}
              />
              <Form.Text className="text-muted">
                Респонденты могут заполнять анкету анонимно
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="require-email"
                label="Требовать email"
                checked={surveyForm.require_email}
                onChange={(e) => handleSurveyFormChange('require_email', e.target.checked)}
              />
              <Form.Text className="text-muted">
                Респонденты должны указать email
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="allow-multiple"
                label="Разрешить несколько отправок"
                checked={surveyForm.allow_multiple_submissions}
                onChange={(e) => handleSurveyFormChange('allow_multiple_submissions', e.target.checked)}
              />
            </Form.Group>
          </Col>
          
          {surveyForm.allow_multiple_submissions && (
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Максимум отправок от одного пользователя</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={surveyForm.max_submissions_per_user}
                  onChange={(e) => handleSurveyFormChange('max_submissions_per_user', parseInt(e.target.value))}
                />
              </Form.Group>
            </Col>
          )}
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Дата начала</Form.Label>
              <Form.Control
                type="datetime-local"
                value={surveyForm.starts_at}
                onChange={(e) => handleSurveyFormChange('starts_at', e.target.value)}
              />
              <Form.Text className="text-muted">
                Если не указано, анкета доступна сразу
              </Form.Text>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Дата окончания</Form.Label>
              <Form.Control
                type="datetime-local"
                value={surveyForm.ends_at}
                onChange={(e) => handleSurveyFormChange('ends_at', e.target.value)}
              />
              <Form.Text className="text-muted">
                Если не указано, анкета доступна бессрочно
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button 
        variant="primary" 
        onClick={handleCreateSurvey}
        disabled={!surveyForm.title || !surveyForm.organization_id}
      >
        <i className="bi bi-check-lg me-2"></i>
        Создать анкету
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно редактирования анкеты
const EditSurveyModal = ({ 
  show, 
  onHide, 
  surveyForm, 
  handleSurveyFormChange, 
  handleUpdateSurvey,
  organizations,
  error,
  selectedSurvey 
}) => (
  <Modal show={show} onHide={onHide} size="lg">
    <Modal.Header closeButton>
      <Modal.Title>
        <i className="bi bi-pencil-square me-2"></i>
        Редактирование анкеты
        {selectedSurvey && (
          <span className="text-muted ms-2">ID: #{selectedSurvey.id}</span>
        )}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      
      <Form>
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Название анкеты</Form.Label>
              <Form.Control
                type="text"
                placeholder="Введите название анкеты"
                value={surveyForm.title}
                onChange={(e) => handleSurveyFormChange('title', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Описание анкеты</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Опишите цель и содержание анкеты"
                value={surveyForm.description}
                onChange={(e) => handleSurveyFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Инструкции для респондентов</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Укажите инструкции по заполнению анкеты"
                value={surveyForm.instructions}
                onChange={(e) => handleSurveyFormChange('instructions', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Организация</Form.Label>
              <Form.Select
                value={surveyForm.organization_id || ''}
                onChange={(e) => handleSurveyFormChange('organization_id', parseInt(e.target.value) || null)}
              >
                <option value="">Выберите организацию</option>
                {organizations.map(org => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={surveyForm.status}
                onChange={(e) => handleSurveyFormChange('status', e.target.value)}
              >
                <option value="draft">Черновик</option>
                <option value="active">Активна</option>
                <option value="archived">Архив</option>
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="edit-is-anonymous"
                label="Анонимная анкета"
                checked={surveyForm.is_anonymous}
                onChange={(e) => handleSurveyFormChange('is_anonymous', e.target.checked)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="edit-require-email"
                label="Требовать email"
                checked={surveyForm.require_email}
                onChange={(e) => handleSurveyFormChange('require_email', e.target.checked)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="edit-allow-multiple"
                label="Разрешить несколько отправок"
                checked={surveyForm.allow_multiple_submissions}
                onChange={(e) => handleSurveyFormChange('allow_multiple_submissions', e.target.checked)}
              />
            </Form.Group>
          </Col>
          
          {surveyForm.allow_multiple_submissions && (
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Максимум отправок от одного пользователя</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={surveyForm.max_submissions_per_user}
                  onChange={(e) => handleSurveyFormChange('max_submissions_per_user', parseInt(e.target.value))}
                />
              </Form.Group>
            </Col>
          )}
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Дата начала</Form.Label>
              <Form.Control
                type="datetime-local"
                value={surveyForm.starts_at}
                onChange={(e) => handleSurveyFormChange('starts_at', e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Дата окончания</Form.Label>
              <Form.Control
                type="datetime-local"
                value={surveyForm.ends_at}
                onChange={(e) => handleSurveyFormChange('ends_at', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button 
        variant="primary" 
        onClick={handleUpdateSurvey}
      >
        <i className="bi bi-check-lg me-2"></i>
        Сохранить изменения
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно удаления анкеты
const DeleteSurveyModal = ({ 
  show, 
  onHide, 
  handleArchiveSurvey,
  selectedSurvey 
}) => (
  <Modal show={show} onHide={onHide}>
    <Modal.Header closeButton>
      <Modal.Title className="text-danger">
        <i className="bi bi-archive me-2"></i>
        Архивировать анкету
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Alert variant="warning" className="mb-3">
        <i className="bi bi-exclamation-triangle me-2"></i>
        Анкета будет перемещена в архив. Вы сможете восстановить её позже.
      </Alert>
      
      {selectedSurvey && (
        <div className="mb-3">
          <p>
            Вы уверены, что хотите переместить в архив анкету 
            <strong> "{selectedSurvey.title}"</strong>?
          </p>
          <div className="alert alert-info">
            <i className="bi bi-info-circle me-2"></i>
            В архиве: {selectedSurvey.submission_count || 0} отправок, 
            {selectedSurvey.questions_count || 0} вопросов
          </div>
        </div>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Отмена
      </Button>
      <Button variant="danger" onClick={handleArchiveSurvey}>
        <i className="bi bi-archive me-2"></i>
        Переместить в архив
      </Button>
    </Modal.Footer>
  </Modal>
);

// Модальное окно деталей анкеты
const SurveyDetailsModal = ({
  show,
  onHide,
  selectedSurvey,
  formatDate,
  getSurveyStatus,
  getSurveyActivity
}) => {
  if (!selectedSurvey) return null;
  
  const status = getSurveyStatus(selectedSurvey);
  const isActive = getSurveyActivity(selectedSurvey);
  
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-info-circle me-2"></i>
          Информация об анкете
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col>
            <h4 className="mb-1">{selectedSurvey.title}</h4>
            <Badge bg={status.variant} className="me-2">
              {status.text}
            </Badge>
            {isActive && (
              <Badge bg="success">
                <i className="bi bi-play-fill me-1"></i>
                Активна
              </Badge>
            )}
            <code className="text-muted ms-2">ID: #{selectedSurvey.id}</code>
          </Col>
        </Row>
        
        <Row className="g-3">
          <Col md={12}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-card-text me-2"></i>
                  Описание анкеты
                </h6>
              </Card.Header>
              <Card.Body>
                {selectedSurvey.description ? (
                  <p className="mb-0">{selectedSurvey.description}</p>
                ) : (
                  <p className="text-muted mb-0">Описание отсутствует</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-building me-2"></i>
                  Организация
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="fw-semibold">{selectedSurvey.organization_name || 'Не указана'}</div>
                {selectedSurvey.public_url && (
                  <div className="mt-2">
                    <small className="text-muted">Публичная ссылка:</small>
                    <div className="input-group input-group-sm">
                      <input 
                        type="text" 
                        className="form-control" 
                        value={selectedSurvey.public_url}
                        readOnly
                      />
                      <button 
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(selectedSurvey.public_url);
                          alert('Ссылка скопирована!');
                        }}
                      >
                        <i className="bi bi-copy"></i>
                      </button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-gear me-2"></i>
                  Настройки
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Тип анкеты:</strong>{' '}
                  {selectedSurvey.is_anonymous ? 'Анонимная' : 'Именная'}
                </div>
                <div className="mb-2">
                  <strong>Email обязателен:</strong>{' '}
                  {selectedSurvey.require_email ? 'Да' : 'Нет'}
                </div>
                <div className="mb-2">
                  <strong>Множественные отправки:</strong>{' '}
                  {selectedSurvey.allow_multiple_submissions ? 'Разрешены' : 'Запрещены'}
                </div>
                {selectedSurvey.allow_multiple_submissions && (
                  <div>
                    <strong>Макс. отправок на пользователя:</strong>{' '}
                    {selectedSurvey.max_submissions_per_user}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-calendar me-2"></i>
                  Период проведения
                </h6>
              </Card.Header>
              <Card.Body>
                {selectedSurvey.starts_at ? (
                  <div className="mb-2">
                    <strong>Дата начала:</strong>
                    <div>{formatDate(selectedSurvey.starts_at)}</div>
                  </div>
                ) : (
                  <div className="mb-2">
                    <strong>Дата начала:</strong>
                    <div className="text-muted">Без ограничений</div>
                  </div>
                )}
                
                {selectedSurvey.ends_at ? (
                  <div>
                    <strong>Дата окончания:</strong>
                    <div>{formatDate(selectedSurvey.ends_at)}</div>
                  </div>
                ) : (
                  <div>
                    <strong>Дата окончания:</strong>
                    <div className="text-muted">Без ограничений</div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={6}>
            <Card className="mb-3">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-bar-chart me-2"></i>
                  Статистика
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Всего вопросов:</strong>{' '}
                  {selectedSurvey.questions_count || 0}
                </div>
                <div className="mb-2">
                  <strong>Всего отправок:</strong>{' '}
                  {selectedSurvey.submission_count || 0}
                </div>
                <div className="mb-2">
                  <strong>Создана:</strong>{' '}
                  {formatDate(selectedSurvey.created_at)}
                </div>
                {selectedSurvey.updated_at && (
                  <div>
                    <strong>Обновлена:</strong>{' '}
                    {formatDate(selectedSurvey.updated_at)}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Модальное окно вопросов анкеты
const SurveyQuestionsModal = ({
  show,
  onHide,
  selectedSurvey,
  selectedSurveyQuestions,
  questionForm,
  handleQuestionFormChange,
  addQuestionOption,
  updateQuestionOption,
  removeQuestionOption,
  handleAddQuestion,
  questionTypes,
  error,
  onDeleteQuestion
}) => {
  const getQuestionTypeLabel = (type) => {
    const typeObj = questionTypes.find(t => t.value === type);
    return typeObj ? typeObj.label : type;
  };
  
  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-question-circle me-2"></i>
          Вопросы анкеты
          {selectedSurvey && (
            <span className="text-muted ms-2">"{selectedSurvey.title}"</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        
        <Row>
          {/* Список существующих вопросов */}
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Существующие вопросы ({selectedSurveyQuestions.length})</h6>
                {selectedSurveyQuestions.length > 0 && (
                  <small className="text-muted">Нажмите для деталей</small>
                )}
              </Card.Header>
              <Card.Body>
                {selectedSurveyQuestions.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bi bi-question-circle display-1 text-muted opacity-50 mb-3"></i>
                    <h6>Вопросов пока нет</h6>
                    <p className="text-muted">Добавьте первый вопрос справа</p>
                  </div>
                ) : (
                  <Accordion>
                    {selectedSurveyQuestions.map((question, index) => (
                      <Accordion.Item key={question.id} eventKey={index.toString()}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between w-100 me-3 align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted small">#{index + 1}</span>
                              <span className="fw-semibold">
                                {question.question_text.length > 50 
                                  ? `${question.question_text.substring(0, 50)}...` 
                                  : question.question_text}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg={question.is_required ? "danger" : "secondary"}>
                                {question.is_required ? 'Обязательный' : 'Необязательный'}
                              </Badge>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteQuestion(question, e);
                                }}
                                title="Удалить вопрос"
                                className="ms-2"
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <div className="mb-2">
                            <strong>Текст вопроса:</strong>
                            <div className="mt-1">{question.question_text}</div>
                          </div>
                          
                          <div className="mb-2">
                            <strong>Тип вопроса:</strong>{' '}
                            {getQuestionTypeLabel(question.question_type)}
                          </div>
                          
                          {question.description && (
                            <div className="mb-2">
                              <strong>Описание:</strong>
                              <div className="text-muted mt-1">{question.description}</div>
                            </div>
                          )}
                          
                          {question.options && question.options.length > 0 && (
                            <div className="mb-2">
                              <strong>Варианты ответа:</strong>
                              <ul className="mb-0 mt-1">
                                {question.options.map((option, optIndex) => (
                                  <li key={optIndex}>{option}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {question.min_length && question.max_length && (
                            <div className="mb-2">
                              <strong>Длина текста:</strong>{' '}
                              от {question.min_length} до {question.max_length} символов
                            </div>
                          )}
                          
                          {question.min_value && question.max_value && (
                            <div className="mb-2">
                              <strong>Диапазон оценки:</strong>{' '}
                              от {question.min_value} до {question.max_value}
                            </div>
                          )}
                          
                          <div className="text-muted small mt-2">
                            <i className="bi bi-arrow-up-down me-1"></i>
                            Порядок: {question.order_index}
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                )}
              </Card.Body>
            </Card>
          </Col>
          
          {/* Форма добавления нового вопроса */}
          <Col md={6}>
            <Card>
              <Card.Header className="bg-light">
                <h6 className="mb-0">Добавить новый вопрос</h6>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Текст вопроса <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Введите текст вопроса"
                      value={questionForm.question_text}
                      onChange={(e) => handleQuestionFormChange('question_text', e.target.value)}
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Описание вопроса (необязательно)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      placeholder="Дополнительное описание вопроса"
                      value={questionForm.description}
                      onChange={(e) => handleQuestionFormChange('description', e.target.value)}
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Тип вопроса</Form.Label>
                        <Form.Select
                          value={questionForm.question_type}
                          onChange={(e) => handleQuestionFormChange('question_type', e.target.value)}
                        >
                          {questionTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Порядковый номер</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={questionForm.order_index}
                          onChange={(e) => handleQuestionFormChange('order_index', parseInt(e.target.value))}
                        />
                        <Form.Text className="text-muted">
                          Чем меньше число, тем выше вопрос
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="switch"
                      id="is-required"
                      label="Обязательный вопрос"
                      checked={questionForm.is_required}
                      onChange={(e) => handleQuestionFormChange('is_required', e.target.checked)}
                    />
                  </Form.Group>
                  
                  {/* Поля для вариантов ответов (для выбора) */}
                  {(questionForm.question_type === 'single_choice' || 
                    questionForm.question_type === 'multiple_choice') && (
                    <Form.Group className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label>Варианты ответов</Form.Label>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={addQuestionOption}
                        >
                          <i className="bi bi-plus"></i> Добавить вариант
                        </Button>
                      </div>
                      
                      {questionForm.options.map((option, index) => (
                        <InputGroup className="mb-2" key={index}>
                          <Form.Control
                            type="text"
                            placeholder={`Вариант ${index + 1}`}
                            value={option}
                            onChange={(e) => updateQuestionOption(index, e.target.value)}
                          />
                          <Button 
                            variant="outline-danger"
                            onClick={() => removeQuestionOption(index)}
                            disabled={questionForm.options.length <= 2}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </InputGroup>
                      ))}
                      
                      <Form.Text className="text-muted">
                        Минимум 2 варианта ответа
                      </Form.Text>
                    </Form.Group>
                  )}
                  
                  {/* Поля для текстового ответа */}
                  {questionForm.question_type === 'text' && (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Минимальная длина</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={questionForm.min_length || ''}
                            onChange={(e) => handleQuestionFormChange('min_length', parseInt(e.target.value) || null)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Максимальная длина</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={questionForm.max_length || ''}
                            onChange={(e) => handleQuestionFormChange('max_length', parseInt(e.target.value) || null)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  {/* Поля для рейтинга */}
                  {questionForm.question_type === 'rating' && (
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Минимальное значение</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            value={questionForm.min_value}
                            onChange={(e) => handleQuestionFormChange('min_value', parseInt(e.target.value))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Максимальное значение</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={questionForm.max_value}
                            onChange={(e) => handleQuestionFormChange('max_value', parseInt(e.target.value))}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  
                  <Button 
                    variant="primary" 
                    className="w-100"
                    onClick={handleAddQuestion}
                    disabled={!questionForm.question_text}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Добавить вопрос
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Модальное окно аналитики анкеты
const SurveyAnalyticsModal = ({
  show,
  onHide,
  selectedSurvey,
  selectedSurveyAnalytics
}) => {
  if (!selectedSurvey) return null;
  
  const renderQuestionStats = (stats) => {
    if (!stats || stats.length === 0) {
      return <Alert variant="info">Нет данных по вопросам</Alert>;
    }
    
    return stats.map((stat, index) => {
      let content;
      
      switch(stat.question_type) {
        case 'single_choice':
        case 'multiple_choice':
          content = (
            <div className="mt-3">
              <strong>Распределение ответов:</strong>
              <div className="mt-2">
                {stat.options && stat.options.map((option, optIndex) => {
                  const count = stat.option_counts?.[option] || 0;
                  const percentage = stat.option_percentages?.[option] || 0;
                  
                  return (
                    <div key={optIndex} className="mb-2">
                      <div className="d-flex justify-content-between">
                        <span className="fw-semibold">{option}</span>
                        <span>
                          {count} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar" 
                          role="progressbar" 
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: `hsl(${optIndex * 40}, 70%, 50%)`
                          }}
                        ></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
          break;
          
        case 'rating':
          content = (
            <div className="mt-3">
              <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Минимальное значение:</strong> {stat.min_value || 1}
                  </div>
                  <div className="mb-2">
                    <strong>Максимальное значение:</strong> {stat.max_value || 5}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Средняя оценка:</strong> {stat.average ? stat.average.toFixed(2) : '0.00'}
                  </div>
                  <div className="mb-2">
                    <strong>Медиана:</strong> {stat.median || '—'}
                  </div>
                </Col>
              </Row>
              
              {stat.distribution && Object.keys(stat.distribution).length > 0 && (
                <div className="mt-3">
                  <strong>Распределение оценок:</strong>
                  <Table bordered size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>Оценка</th>
                        <th>Количество</th>
                        <th>Процент</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(stat.distribution).map(([rating, count]) => {
                        const percentage = (count / stat.total_answers * 100).toFixed(1);
                        return (
                          <tr key={rating}>
                            <td>{rating}</td>
                            <td>{count}</td>
                            <td>{percentage}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          );
          break;
          
        case 'text':
          content = (
            <div className="mt-3">
              <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Минимальная длина:</strong> {stat.min_length || '—'}
                  </div>
                  <div className="mb-2">
                    <strong>Максимальная длина:</strong> {stat.max_length || '—'}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-2">
                    <strong>Средняя длина:</strong> {stat.avg_length ? stat.avg_length.toFixed(0) + ' символов' : '—'}
                  </div>
                  <div className="mb-2">
                    <strong>Всего ответов:</strong> {stat.total_answers}
                  </div>
                </Col>
              </Row>
              
              {stat.sample_answers && stat.sample_answers.length > 0 && (
                <div className="mt-3">
                  <strong>Примеры ответов ({stat.sample_answers.length} из {stat.total_answers}):</strong>
                  <div className="mt-2">
                    {stat.sample_answers.map((answer, ansIndex) => (
                      <div key={ansIndex} className="mb-2 p-3 bg-light rounded">
                        <div className="d-flex justify-content-between align-items-start">
                          <span className="me-3">{answer}</span>
                          <Badge bg="secondary" pill>
                            {answer.length} симв.
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
          break;
          
        case 'yes_no':
          content = (
            <div className="mt-3">
              <Row>
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                    <strong>Да:</strong>
                    <span className="ms-2">
                      {stat.option_counts?.Да || 0} ({stat.option_percentages?.Да ? stat.option_percentages.Да.toFixed(1) : '0'}%)
                    </span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-x-circle-fill text-danger me-2"></i>
                    <strong>Нет:</strong>
                    <span className="ms-2">
                      {stat.option_counts?.Нет || 0} ({stat.option_percentages?.Нет ? stat.option_percentages.Нет.toFixed(1) : '0'}%)
                    </span>
                  </div>
                </Col>
              </Row>
            </div>
          );
          break;
          
        default:
          content = (
            <div className="mt-3">
              <Alert variant="warning">
                Неизвестный тип вопроса: {stat.question_type}
              </Alert>
            </div>
          );
      }
      
      return (
        <Card key={index} className="mb-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 className="mb-1">
                  Вопрос {index + 1}: {stat.question_text}
                </h6>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg={stat.required ? "danger" : "secondary"}>
                    {stat.required ? 'Обязательный' : 'Необязательный'}
                  </Badge>
                  <Badge bg="info">
                    {stat.question_type === 'single_choice' ? 'Одиночный выбор' :
                     stat.question_type === 'multiple_choice' ? 'Множественный выбор' :
                     stat.question_type === 'text' ? 'Текстовый ответ' :
                     stat.question_type === 'rating' ? 'Оценка' :
                     stat.question_type === 'yes_no' ? 'Да/Нет' : stat.question_type}
                  </Badge>
                </div>
              </div>
              <div className="text-end">
                <div className="text-primary fw-bold fs-5">{stat.total_answers || 0}</div>
                <small className="text-muted">ответов</small>
              </div>
            </div>
            
            <div className="mt-3">
              {stat.description && (
                <div className="mb-2">
                  <small className="text-muted">{stat.description}</small>
                </div>
              )}
              
              {content}
            </div>
          </Card.Body>
        </Card>
      );
    });
  };
  
  const renderSubmissionsOverTime = (overTimeData) => {
    if (!overTimeData || Object.keys(overTimeData).length === 0) {
      return null;
    }
    
    const dates = Object.keys(overTimeData).sort();
    const counts = dates.map(date => overTimeData[date]);
    const maxCount = Math.max(...counts);
    
    return (
      <Card className="mt-4">
        <Card.Header className="bg-light">
          <h6 className="mb-0">
            <i className="bi bi-calendar-week me-2"></i>
            Динамика отправок по дням
          </h6>
        </Card.Header>
        <Card.Body>
          <div style={{ height: '300px' }}>
            <div className="overflow-auto">
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Количество отправок</th>
                    <th>Гистограмма</th>
                  </tr>
                </thead>
                <tbody>
                  {dates.map((date, index) => {
                    const count = counts[index];
                    const percentage = maxCount > 0 ? (count / maxCount * 100) : 0;
                    
                    return (
                      <tr key={date}>
                        <td>
                          {new Date(date).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="fw-bold">{count}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div 
                              className="bg-primary rounded me-2"
                              style={{ 
                                width: `${percentage}%`, 
                                height: '20px',
                                minWidth: '10px'
                              }}
                            ></div>
                            <small className="text-muted">{percentage.toFixed(0)}%</small>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            
            <div className="mt-3">
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-graph-up text-primary fs-4"></i>
                    </div>
                    <div>
                      <div className="text-muted small">Всего за период</div>
                      <div className="fs-5 fw-bold">
                        {Object.values(overTimeData).reduce((a, b) => a + b, 0)} отправок
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <i className="bi bi-calendar-range text-success fs-4"></i>
                    </div>
                    <div>
                      <div className="text-muted small">Период</div>
                      <div className="fs-5 fw-bold">
                        {dates.length} дней
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };
  
  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <i className="bi bi-graph-up me-2"></i>
          Аналитика анкеты
          {selectedSurvey && (
            <span className="text-muted ms-2">"{selectedSurvey.title}"</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!selectedSurveyAnalytics ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-3" />
            <p className="text-muted">Загрузка аналитики...</p>
            <Button 
              variant="outline-primary"
              onClick={() => window.location.reload()}
            >
              Обновить
            </Button>
          </div>
        ) : (
          <>
            {/* Основная информация об анкете */}
            <Card className="mb-4">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Информация об анкете
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="text-muted small">Название</div>
                      <div className="fw-semibold">{selectedSurvey.title}</div>
                    </div>
                    <div className="mb-3">
                      <div className="text-muted small">Организация</div>
                      <div className="fw-semibold">{selectedSurvey.organization_name || '—'}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="text-muted small">Всего отправок</div>
                      <div className="fs-4 fw-bold text-primary">
                        {selectedSurveyAnalytics.total_submissions || 0}
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="text-muted small">Уникальных участников</div>
                      <div className="fw-semibold">
                        {selectedSurveyAnalytics.unique_respondents || 0}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
            
            {/* Динамика отправок */}
            {selectedSurveyAnalytics.submissions_over_time && 
             Object.keys(selectedSurveyAnalytics.submissions_over_time).length > 0 && 
             renderSubmissionsOverTime(selectedSurveyAnalytics.submissions_over_time)}
            
            {/* Статистика по вопросам */}
            <h5 className="mt-4 mb-3">Статистика по вопросам</h5>
            {renderQuestionStats(selectedSurveyAnalytics.question_stats)}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Модальное окно отправок анкеты
const SurveySubmissionsModal = ({
  show,
  onHide,
  selectedSurvey,
  selectedSurveySubmissions,
  formatDate
}) => {
  if (!selectedSurvey) return null;
  
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  
  const toggleExpanded = (submissionId) => {
    if (expandedSubmission === submissionId) {
      setExpandedSubmission(null);
    } else {
      setExpandedSubmission(submissionId);
    }
  };
  
  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-list-check me-2"></i>
          Отправки анкеты
          {selectedSurvey && (
            <span className="text-muted ms-2">"{selectedSurvey.title}"</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedSurveySubmissions.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox display-1 text-muted opacity-50 mb-3"></i>
            <h5>Отправок пока нет</h5>
            <p className="text-muted">Респонденты еще не заполняли эту анкету</p>
          </div>
        ) : (
          <div className="submissions-list">
            {selectedSurveySubmissions.map((submission) => (
              <Card key={submission.id} className="mb-3">
                <Card.Header 
                  className="bg-light d-flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => toggleExpanded(submission.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <strong>Отправка #{submission.id}</strong>
                    <span className="text-muted ms-3">
                      {formatDate(submission.created_at)}
                    </span>
                    {submission.completion_time_seconds && (
                      <Badge bg="info" className="ms-2">
                        {Math.floor(submission.completion_time_seconds / 60)} мин {submission.completion_time_seconds % 60} сек
                      </Badge>
                    )}
                  </div>
                  <i className={`bi bi-chevron-${expandedSubmission === submission.id ? 'up' : 'down'}`}></i>
                </Card.Header>
                {expandedSubmission === submission.id && (
                  <Card.Body>
                    {submission.respondent_name && (
                      <div className="mb-3">
                        <div className="text-muted small">Имя респондента</div>
                        <div className="fw-semibold">{submission.respondent_name}</div>
                      </div>
                    )}
                    {submission.respondent_email && (
                      <div className="mb-3">
                        <div className="text-muted small">Email</div>
                        <div className="fw-semibold">{submission.respondent_email}</div>
                      </div>
                    )}
                    
                    <h6 className="mt-3 mb-2">Ответы на вопросы:</h6>
                    {submission.answers && submission.answers.length > 0 ? (
                      <div className="answers-list">
                        {submission.answers.map((answer, index) => (
                          <div key={answer.id || index} className="mb-3 p-3 bg-light rounded">
                            <div className="fw-semibold mb-2">
                              Вопрос {index + 1}: {answer.question_text}
                            </div>
                            <div className="answer-content">
                              {answer.text_answer && (
                                <div>
                                  <span className="text-muted small">Ответ:</span>
                                  <div className="mt-1">{answer.text_answer}</div>
                                </div>
                              )}
                              {answer.selected_option && (
                                <div>
                                  <span className="text-muted small">Выбранный вариант:</span>
                                  <div className="mt-1 fw-semibold">{answer.selected_option}</div>
                                </div>
                              )}
                              {answer.selected_options && answer.selected_options.length > 0 && (
                                <div>
                                  <span className="text-muted small">Выбранные варианты:</span>
                                  <div className="mt-1">
                                    {answer.selected_options.map((opt, i) => (
                                      <Badge key={i} bg="primary" className="me-1">
                                        {opt}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {answer.rating_value !== null && answer.rating_value !== undefined && (
                                <div>
                                  <span className="text-muted small">Оценка:</span>
                                  <div className="mt-1">
                                    {Array.from({ length: answer.rating_value }, (_, i) => (
                                      <i key={i} className="bi bi-star-fill text-warning me-1"></i>
                                    ))}
                                    {Array.from({ length: 5 - answer.rating_value }, (_, i) => (
                                      <i key={i} className="bi bi-star text-warning me-1"></i>
                                    ))}
                                    <span className="ms-2 fw-semibold">{answer.rating_value} из 5</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">Нет ответов на вопросы</p>
                    )}
                    
                    <div className="text-muted small mt-3">
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Код отправки: <code>{submission.submission_code}</code>
                    </div>
                  </Card.Body>
                )}
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Модальное окно QR-кода
const QRCodeModal = ({
  show,
  onHide,
  qrCodeUrl,
  selectedSurvey
}) => {
  const [qrImageUrl, setQrImageUrl] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const qrImageRef = useRef(null);

  useEffect(() => {
    if (qrCodeUrl && show) {
      // Генерируем QR-код через API с увеличенным размером для лучшего качества
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeUrl)}`;
      setQrImageUrl(qrApiUrl);
    }
  }, [qrCodeUrl, show]);

  const copyLink = () => {
    navigator.clipboard.writeText(qrCodeUrl);
    alert('Ссылка скопирована в буфер обмена!');
  };

  // Функция для скачивания QR-кода
  const downloadQRCode = async () => {
    if (!qrImageUrl) return;
    
    setIsDownloading(true);
    
    try {
      // Создаем canvas для лучшего контроля над изображением
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // Для CORS
      
      // Ждем загрузки изображения
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = qrImageUrl;
      });
      
      // Создаем canvas с увеличенным размером
      const canvas = document.createElement('canvas');
      const size = 500; // Увеличиваем размер для лучшего качества
      canvas.width = size;
      canvas.height = size;
      
      const ctx = canvas.getContext('2d');
      
      // Белый фон
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      
      // Рисуем QR-код с отступами
      const padding = 20;
      const qrSize = size - (padding * 2);
      ctx.drawImage(img, padding, padding, qrSize, qrSize);
      
      // Добавляем текст с названием анкеты под QR-кодом
      if (selectedSurvey?.title) {
        ctx.font = 'bold 14px "Segoe UI", system-ui, sans-serif';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'center';
        
        // Обрезаем длинное название
        let title = selectedSurvey.title;
        if (title.length > 40) {
          title = title.substring(0, 37) + '...';
        }
        
        ctx.fillText(title, size / 2, size - 8);
      }
      
      // Конвертируем canvas в blob и скачиваем
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const filename = `qr-${selectedSurvey?.title || 'survey'}-${selectedSurvey?.id || Date.now()}.png`;
        link.download = filename;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }, 'image/png');
      
    } catch (error) {
      console.error('Error downloading QR code:', error);
      alert('Не удалось скачать QR-код. Попробуйте еще раз.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-qr-code me-2"></i>
          QR-код для анкеты
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        {selectedSurvey && (
          <div className="mb-3">
            <h6 className="mb-1">{selectedSurvey.title}</h6>
            <small className="text-muted">Отсканируйте QR-код для перехода к анкете</small>
          </div>
        )}
        
        <div className="qr-code-container d-flex justify-content-center mb-4">
          {qrImageUrl ? (
            <img 
              ref={qrImageRef}
              src={qrImageUrl} 
              alt="QR Code"
              className="img-fluid border p-3 rounded shadow-sm"
              style={{ 
                maxWidth: '280px', 
                backgroundColor: 'white',
                borderRadius: '16px !important'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                console.error('Failed to load QR code');
              }}
            />
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Генерация QR-кода...</div>
            </div>
          )}
        </div>
        
        <div className="mt-3">
          <small className="text-muted d-block mb-2">Или скопируйте ссылку:</small>
          <div className="input-group">
            <Form.Control
              type="text"
              value={qrCodeUrl}
              readOnly
              size="sm"
              className="bg-light"
            />
            <Button variant="outline-secondary" onClick={copyLink}>
              <i className="bi bi-copy"></i>
            </Button>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={onHide}>
          Закрыть
        </Button>
        <Button 
          variant="primary" 
          onClick={downloadQRCode}
          disabled={isDownloading || !qrImageUrl}
          className="d-flex align-items-center gap-2"
        >
          {isDownloading ? (
            <>
              <Spinner animation="border" size="sm" />
              <span>Скачивание...</span>
            </>
          ) : (
            <>
              <i className="bi bi-download"></i>
              <span>Скачать QR-код</span>
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};


export default SurveysTab;